import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { blogPosts } from '@/mocks/blog';

export default function BlogPost() {
  const { slug } = useParams<{ slug: string }>();
  const post = blogPosts.find((p) => p.slug === slug);
  const related = blogPosts.filter((p) => p.slug !== slug).slice(0, 3);

  if (!post) {
    return (
      <div className="min-h-screen bg-white font-sans">
        <Navbar />
        <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
          <i className="ri-article-line text-5xl text-[#1C1C1C]/15"></i>
          <p className="text-[#1C1C1C]/40">სტატია ვერ მოიძებნა</p>
          <Link to="/blog" className="text-sm font-bold text-[#ec3145] hover:underline cursor-pointer">
            ← ბლოგზე დაბრუნება
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  // Render markdown-like content
  const renderContent = (content: string) => {
    return content.split('\n').map((line, i) => {
      if (line.startsWith('**') && line.endsWith('**')) {
        return <h3 key={i} className="text-base font-bold text-[#1C1C1C] mt-6 mb-2">{line.replace(/\*\*/g, '')}</h3>;
      }
      if (line.match(/^\d+\.\s/)) {
        return <li key={i} className="text-sm text-[#1C1C1C]/65 leading-relaxed ml-4 list-decimal">{line.replace(/^\d+\.\s/, '')}</li>;
      }
      if (line.startsWith('- ')) {
        return <li key={i} className="text-sm text-[#1C1C1C]/65 leading-relaxed ml-4 list-disc">{line.replace(/^- /, '')}</li>;
      }
      if (line.trim() === '') {
        return <div key={i} className="h-3"></div>;
      }
      // Inline bold
      const parts = line.split(/(\*\*[^*]+\*\*)/g);
      return (
        <p key={i} className="text-sm text-[#1C1C1C]/65 leading-relaxed">
          {parts.map((part, j) =>
            part.startsWith('**') && part.endsWith('**')
              ? <strong key={j} className="font-bold text-[#1C1C1C]/80">{part.replace(/\*\*/g, '')}</strong>
              : part
          )}
        </p>
      );
    });
  };

  return (
    <div className="min-h-screen bg-white font-sans">
      <Navbar />

      {/* Hero image */}
      <div className="relative w-full overflow-hidden" style={{ height: '480px', marginTop: '64px' }}>
        <img
          src={post.image}
          alt={post.title}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1C1C1C]/80 via-[#1C1C1C]/30 to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 max-w-4xl mx-auto px-8 md:px-14 pb-10">
          <span className="inline-block text-[9px] font-bold tracking-[0.25em] uppercase text-white bg-[#ec3145] px-3 py-1.5 mb-4">
            {post.category}
          </span>
          <h1 className="text-2xl md:text-4xl font-bold text-white leading-tight mb-4 max-w-2xl">
            {post.title}
          </h1>
          <div className="flex flex-wrap items-center gap-4 text-xs text-white/50">
            <span className="flex items-center gap-1.5">
              <i className="ri-user-line"></i>
              {post.author} — {post.authorRole}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line"></i>
              {post.date}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-time-line"></i>
              {post.readTime} წუთი კითხვა
            </span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-8 md:px-14 py-14 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_260px] gap-12">

          {/* Article body */}
          <article>
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-xs text-[#1C1C1C]/30 mb-8">
              <Link to="/" className="hover:text-[#ec3145] transition-colors cursor-pointer">მთავარი</Link>
              <i className="ri-arrow-right-s-line text-[10px]"></i>
              <Link to="/blog" className="hover:text-[#ec3145] transition-colors cursor-pointer">ბლოგი</Link>
              <i className="ri-arrow-right-s-line text-[10px]"></i>
              <span className="text-[#1C1C1C]/50 line-clamp-1">{post.title}</span>
            </nav>

            {/* Excerpt */}
            <p className="text-base md:text-lg text-[#1C1C1C]/55 leading-relaxed mb-8 pb-8 border-b border-[#1C1C1C]/8 font-medium">
              {post.excerpt}
            </p>

            {/* Body */}
            <div className="flex flex-col gap-1">
              {renderContent(post.content)}
            </div>

            {/* Tags */}
            <div className="mt-10 pt-8 border-t border-[#1C1C1C]/8">
              <p className="text-[10px] font-bold tracking-[0.25em] uppercase text-[#1C1C1C]/30 mb-3">თეგები</p>
              <div className="flex flex-wrap gap-2">
                {post.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1.5 bg-[#F5F2EE] text-xs font-semibold text-[#1C1C1C]/55">
                    #{tag}
                  </span>
                ))}
              </div>
            </div>

            {/* Author */}
            <div className="mt-8 p-6 bg-[#F5F2EE] flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-[#ec3145]/10 flex items-center justify-center flex-shrink-0">
                <i className="ri-user-line text-[#ec3145] text-lg"></i>
              </div>
              <div>
                <p className="text-sm font-bold text-[#1C1C1C]">{post.author}</p>
                <p className="text-xs text-[#1C1C1C]/45">{post.authorRole} — Phoenix Georgia</p>
              </div>
            </div>

            {/* Back */}
            <div className="mt-8">
              <Link
                to="/blog"
                className="inline-flex items-center gap-2 text-sm font-bold text-[#ec3145] hover:text-[#c9283a] transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                ბლოგზე დაბრუნება
              </Link>
            </div>
          </article>

          {/* Sidebar */}
          <aside className="hidden lg:block">
            <div className="sticky top-24 flex flex-col gap-6">
              {/* Related posts */}
              <div>
                <h3 className="text-[10px] font-bold tracking-[0.3em] uppercase text-[#1C1C1C]/30 mb-4">სხვა სტატიები</h3>
                <div className="flex flex-col gap-4">
                  {related.map((r) => (
                    <Link
                      key={r.id}
                      to={`/blog/${r.slug}`}
                      className="group flex gap-3 cursor-pointer"
                    >
                      <div className="w-16 h-16 flex-shrink-0 overflow-hidden bg-[#F5F2EE]">
                        <img src={r.image} alt={r.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] font-bold text-[#ec3145] mb-0.5">{r.category}</p>
                        <p className="text-xs font-semibold text-[#1C1C1C]/70 group-hover:text-[#ec3145] transition-colors leading-snug line-clamp-2">
                          {r.title}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>

              {/* CTA */}
              <div className="bg-[#1C1C1C] p-5">
                <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-[#ec3145] mb-2">კონსულტაცია</p>
                <p className="text-xs text-white/60 leading-relaxed mb-4">
                  გჭირდებათ ტექნიკური დახმარება? ჩვენი სპეციალისტები მზად არიან.
                </p>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-1.5 text-[10px] font-bold tracking-[0.15em] uppercase text-white border border-white/20 px-4 py-2.5 hover:bg-white hover:text-[#1C1C1C] transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  დაგვიკავშირდით
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <Footer />
    </div>
  );
}